import { selectUserContext } from 'core/userContext';
import { useQuickActionsMenuContext } from 'modules/quickActionsMenu/contexts';
import { NavigationItem } from 'modules/quickActionsMenu/types';
import { defineExternalLink, doesUserHaveAccess } from 'modules/quickActionsMenu/utils';
import { useSelector } from 'react-redux';
import { KeyEnter, LongArrowRight16 } from 'ui/icons';
import { ORG_PERMISSIONS, PROJECT_PERMISSIONS } from 'utils/permissions';
import { Action, Item } from '../Item';
import { CommandGroup } from '../Styled';

export const NAVIGATION_GROUP_TEXT = {
  heading: 'Pages',
};

const { heading } = NAVIGATION_GROUP_TEXT;

export const NAVIGATION_GROUP_PAGES: NavigationItem[] = [
  { name: 'Account Settings', path: '/preferences' },
  {
    featureFlags: ['CampaignPerformanceAlerts'],
    name: 'Alerts',
    path: '/settings/alerts/campaigns',
    permissions: [PROJECT_PERMISSIONS.ManageProjectSettings],
  },
  {
    featureFlags: ['AnonymousUserCriteria'],
    name: 'Unknown User Activation',
    path: '/users/unknownUserActivation',
    permissions: [PROJECT_PERMISSIONS.ManageSegmentation],
  },
  {
    name: 'API Keys',
    path: '/settings/apiKeys',
    permissions: [PROJECT_PERMISSIONS.ManageIntegrations],
  },
  {
    name: 'Apps and Websites',
    path: '/integrations/mobileApps',
    permissions: [PROJECT_PERMISSIONS.ViewChannelConfiguration],
  },
  {
    name: 'Authentication',
    path: '/settings/authentication',
    permissions: [ORG_PERMISSIONS.ManageMembers],
  },
  {
    featureFlags: ['MetadataStoreUI'],
    name: 'Catalogs',
    path: '/catalogs',
    permissions: [PROJECT_PERMISSIONS.ViewCatalogs],
  },
  {
    featureFlags: ['CatalogQueries'],
    name: 'Collections',
    path: '/catalogs/collections',
    permissions: [PROJECT_PERMISSIONS.ViewCatalogs],
  },
  { name: 'Creative Library', path: '/creativeLibrary', featureFlags: ['AssetManagerIndex'] },
  { name: 'Dashboard', path: '/dashboard' },
  {
    name: 'Data Feeds',
    path: '/integrations/dataFeeds',
    permissions: [PROJECT_PERMISSIONS.ManageIntegrations],
  },
  {
    featureFlags: ['SchemaManagementUI'],
    name: 'Data Schema Management',
    path: '/settings/dataSchemaManagement',
    permissions: [PROJECT_PERMISSIONS.ManageProjectSettings],
  },
  {
    name: 'DNS Setup',
    path: '/settings/dnsSetup',
    permissions: [PROJECT_PERMISSIONS.ViewChannelConfiguration],
  },
  {
    name: 'Domains',
    path: '/settings/domains',
    permissions: [PROJECT_PERMISSIONS.ViewChannelConfiguration],
  },
  { name: 'Events', path: '/events', permissions: [PROJECT_PERMISSIONS.ViewAndExportUserData] },
  { name: 'Experiments', path: '/experiments', permissions: [PROJECT_PERMISSIONS.ViewMessaging] },
  {
    name: 'Facebook Integrations',
    path: '/integrations/facebookIntegration',
    permissions: [PROJECT_PERMISSIONS.ManageIntegrations],
  },
  {
    name: 'Inbound SMS',
    path: '/users/inboundSMS',
    permissions: [PROJECT_PERMISSIONS.ViewAndExportUserData],
  },
  {
    name: 'Journey Webhooks',
    path: '/workflows/webhooks',
    permissions: [PROJECT_PERMISSIONS.ManageIntegrations],
  },
  {
    featureFlags: ['LinkExpirationManagement'],
    name: 'Link Expiration Management',
    path: '/settings/linkExpirationManagement',
    permissions: Object.values(ORG_PERMISSIONS), // require all org permissions as proxy for `orgAdmin` boolean
  },
  { name: 'Lists', path: '/lists' },
  {
    name: 'Message Channels and Types',
    path: '/settings/channels/email',
    permissions: [PROJECT_PERMISSIONS.ViewChannelConfiguration],
  },
  {
    name: 'Messaging Insights',
    path: '/messagingInsights',
    permissions: [PROJECT_PERMISSIONS.ViewReports],
  },
  { name: 'Organization Members', path: '/members', permissions: [ORG_PERMISSIONS.ManageMembers] },
  {
    featureFlags: ['PredictiveGoals'],
    name: 'Predictive Goals',
    path: '/predictive-audiences',
    permissions: [PROJECT_PERMISSIONS.ViewGoals],
  },
  {
    name: 'Project Members',
    path: '/projects/:projectId',
    permissions: [ORG_PERMISSIONS.ManageMembers],
  },
  {
    // only isExternal if ProjectSettingsModernization feature flag is not enabled
    isExternal: (flags) => !flags.ProjectSettingsModernization,
    name: 'Project Settings',
    path: '/settings/project',
    permissions: [PROJECT_PERMISSIONS.ManageProjectSettings],
  },
  {
    name: 'Purchases',
    path: '/purchases',
    permissions: [PROJECT_PERMISSIONS.ViewAndExportUserData],
  },
  { name: 'Reports', path: '/reports', permissions: [PROJECT_PERMISSIONS.ViewReports] },
  { name: 'Roles', path: '/roles', permissions: [ORG_PERMISSIONS.ManageRoles] },
  {
    name: 'Scheduled Messages',
    path: '/triggered/scheduledMessages',
    permissions: [PROJECT_PERMISSIONS.ViewMessaging, PROJECT_PERMISSIONS.ViewAndExportUserData],
  },
  { name: 'Segmentation', path: '/segmentation', isExternal: true },
  { name: 'Send Calendar', path: '/campaigns/calendar', isExternal: true },
  {
    isExternal: true,
    name: 'Senders',
    path: '/settings/senders',
    permissions: [PROJECT_PERMISSIONS.ViewChannelConfiguration],
  },
  {
    name: 'Sending Platforms',
    path: '/settings/sendingPlatforms',
    permissions: [PROJECT_PERMISSIONS.ViewChannelConfiguration],
  },
  {
    isExternal: true,
    name: 'Sent Messages',
    path: '/campaigns/triggeredSendLog',
    permissions: [PROJECT_PERMISSIONS.ViewMessaging, PROJECT_PERMISSIONS.ViewAndExportUserData],
  },
  {
    featureFlags: ['HightouchIntegration'],
    name: 'Smart Ingest',
    path: '/integrations/smartIngest',
    permissions: [PROJECT_PERMISSIONS.ManageIntegrations],
  },
  {
    name: 'System Webhooks',
    path: '/integrations/webhooks',
    permissions: [PROJECT_PERMISSIONS.ManageIntegrations],
  },
  {
    name: 'Third Party Integrations',
    path: '/integrations/thirdParty',
    permissions: [PROJECT_PERMISSIONS.ManageIntegrations],
  },
  {
    /**
     * this is not an external page. but for the moment, we want to do a hard refresh on this page.
     * see: https://iterable.atlassian.net/browse/EN-167
     */
    isExternal: true,
    name: 'Usage and Billing',
    path: '/payments/info',
    permissions: [ORG_PERMISSIONS.ManageBilling],
  },
  {
    name: 'Web Push',
    path: '/integrations/webPush',
    permissions: [PROJECT_PERMISSIONS.ViewChannelConfiguration],
  },
  {
    featureFlags: ['WhatsAppMessageChannel', 'WhatsAppMessageChannelGA'],
    requireOnlyOneFlag: true,
    name: 'WhatsApp Message',
    path: '/whatsAppMessages',
    permissions: [PROJECT_PERMISSIONS.ViewMessaging, PROJECT_PERMISSIONS.ViewAndExportUserData],
  },
];

export const NavigationGroup = () => {
  const { navigate } = useQuickActionsMenuContext();
  const { featureFlags = {}, rbacPermissions = [] } = useSelector(selectUserContext);
  const filteredPages = NAVIGATION_GROUP_PAGES.filter((page) =>
    doesUserHaveAccess(page, rbacPermissions, featureFlags),
  );

  return (
    <CommandGroup heading={heading}>
      {filteredPages.map((page) => {
        const isExternal = defineExternalLink(page?.isExternal, featureFlags);

        return (
          <Item
            Icon={<LongArrowRight16 />}
            key={page.path}
            onSelect={() => {
              navigate({ pageName: page.name, path: page.path, isExternal });
            }}
            name={page.name}
            value={`${page.path} ${page.name}`}
          >
            <Action prefix="Press" Key={<KeyEnter isColorInherited />} action="open" />
          </Item>
        );
      })}
    </CommandGroup>
  );
};
